// A wallet can expose throwing Proxy getters; optional chaining does not catch them.
export function watchWallet(provider, handlers, onUnavailable, refresh) {
  let active = true;
  let polling = false;
  const subscriptions = [];
  const report = () => { if (active) onUnavailable(); };
  const cleanSubscriptions = () => {
    for (const [event, listener] of subscriptions.splice(0)) {
      try { provider.removeListener?.(event, listener); } catch { /* Broken external provider. */ }
    }
  };
  const guarded = handler => (...args) => { if (active) handler(...args); };
  try {
    const on = provider.on;
    if (typeof on !== 'function') throw new Error('Wallet events unavailable');
    for (const [event, handler] of Object.entries(handlers)) {
      const listener = guarded(handler);
      subscriptions.push([event, listener]);
      on.call(provider, event, listener);
    }
  } catch {
    cleanSubscriptions();
    report();
    polling = true;
  }
  let pending = false;
  const check = async () => {
    if (!active || pending || document.hidden) return;
    pending = true;
    try { await refresh(); } catch { report(); } finally { pending = false; }
  };
  const timer = polling ? setInterval(check, 5000) : null;
  if (polling) window.addEventListener('focus', check);
  return () => {
    active = false;
    clearInterval(timer);
    window.removeEventListener('focus', check);
    cleanSubscriptions();
  };
}

export async function assertWalletContext(provider, account, chainId) {
  const [accounts, chain] = await Promise.all([
    provider.request({ method: 'eth_accounts' }),
    provider.request({ method: 'eth_chainId' }),
  ]);
  if (!account || accounts?.[0]?.toLowerCase() !== account.toLowerCase()) {
    throw new Error('Wallet account changed. Reconnect your wallet before continuing.');
  }
  if (String(chain).toLowerCase() !== chainId.toLowerCase()) {
    throw new Error('Wrong network. Switch to Optimism first.');
  }
}
